import { FC } from 'react';
import { Input, Select, Typography } from 'antd';
import { StreetAddress, AddressFieldTypeProps } from '#types';
import { COMMON_COUNTRIES } from '#types/System.Parameters.types';
import FlexCol from '../../../atoms/FlexCol';
import FlexBox from '../../../atoms/FlexBox';

const { Text } = Typography;

/**
 * Props for the DetailedAddressForm component
 */
interface DetailedAddressFormProps extends AddressFieldTypeProps {
  /** Current address value with components */
  addressValue: StreetAddress;
  /** Whether the form inputs are disabled */
  disabled?: boolean;
  /** Validation status for the inputs */
  status?: '' | 'error' | 'warning' | undefined;
  /** Callback when an address component changes */
  onComponentChange: (field: keyof NonNullable<StreetAddress['components']>, newValue: string) => void;
}

/**
 * Detailed address form for manual entry of address components, ordered as a postal label:
 * who receives it, then where it goes, then how to reach them.
 * `showRecipient` and `showContact` read as true when absent, so a field type that says nothing
 * offers the whole destination.
 */
const DetailedAddressForm: FC<DetailedAddressFormProps> = ({
  addressValue,
  disabled = false,
  status,
  onComponentChange,
  showRecipient = true,
  showContact = true
}) => {
  const location = addressValue.alternativeData?.location;
  const hasCoordinates = location?.latitude && location?.longitude;

  return (
    <FlexCol gap={10} style={{ width: 400, maxWidth: '90vw' }}>
      {showRecipient && (
        <>
          <FlexCol gap={0}>
            <Text>Recipient</Text>
            <Input
              value={addressValue.components?.recipientName || ''}
              onChange={(e) => onComponentChange('recipientName', e.target.value)}
              disabled={disabled}
              placeholder="Dana Reyes"
              aria-label="address-recipient-name-input"
            />
          </FlexCol>

          <FlexCol gap={0}>
            <Text>Company</Text>
            <Input
              value={addressValue.components?.company || ''}
              onChange={(e) => onComponentChange('company', e.target.value)}
              disabled={disabled}
              placeholder="Acme Lighting Co."
              aria-label="address-company-input"
            />
          </FlexCol>
        </>
      )}

      <FlexCol gap={0}>
        <Text>Street Address</Text>
        <Input
          value={addressValue.components?.street || ''}
          onChange={(e) => onComponentChange('street', e.target.value)}
          disabled={disabled}
          status={status}
          placeholder="123 Main St"
          aria-label="address-street-input"
        />
      </FlexCol>

      <FlexCol gap={0}>
        <Text>Apt, Suite, etc. (Optional)</Text>
        <Input
          value={addressValue.components?.street2 || ''}
          onChange={(e) => onComponentChange('street2', e.target.value)}
          disabled={disabled}
          placeholder="Apt 4B"
          aria-label="address-street2-input"
        />
      </FlexCol>

      <FlexBox>
        <FlexCol gap={0} style={{ flex: 2 }}>
          <Text>City</Text>
          <Input
            value={addressValue.components?.city || ''}
            onChange={(e) => onComponentChange('city', e.target.value)}
            disabled={disabled}
            status={status}
            placeholder="New York"
            aria-label="address-city-input"
          />
        </FlexCol>

        <FlexCol gap={0} style={{ flex: 1 }}>
          <Text>State/Province</Text>
          <Input
            value={addressValue.components?.state || ''}
            onChange={(e) => onComponentChange('state', e.target.value)}
            disabled={disabled}
            placeholder="NY"
            aria-label="address-state-input"
          />
        </FlexCol>
      </FlexBox>

      <FlexBox>
        <FlexCol gap={0} style={{ flex: 1 }}>
          <Text>Postal Code</Text>
          <Input
            value={addressValue.components?.postalCode || ''}
            onChange={(e) => onComponentChange('postalCode', e.target.value)}
            disabled={disabled}
            placeholder="10001"
            aria-label="address-postalcode-input"
          />
        </FlexCol>

        <FlexCol gap={0} style={{ flex: 2 }}>
          <Text>Country</Text>
          <Select
            value={addressValue.components?.country || undefined}
            onChange={(newValue) => onComponentChange('country', newValue)}
            disabled={disabled}
            status={status}
            placeholder="Select country"
            options={COMMON_COUNTRIES}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            aria-label="address-country-select"
            style={{ width: '100%' }}
          />
        </FlexCol>
      </FlexBox>

      {showContact && (
        <FlexBox>
          <FlexCol gap={0} style={{ flex: 1 }}>
            <Text>Phone</Text>
            <Input
              value={addressValue.components?.phone || ''}
              onChange={(e) => onComponentChange('phone', e.target.value)}
              disabled={disabled}
              placeholder="(312) 555-0142"
              aria-label="address-phone-input"
            />
          </FlexCol>

          <FlexCol gap={0} style={{ flex: 1 }}>
            <Text>Email</Text>
            <Input
              value={addressValue.components?.email || ''}
              onChange={(e) => onComponentChange('email', e.target.value)}
              disabled={disabled}
              placeholder="dana@acmelighting.com"
              aria-label="address-email-input"
            />
          </FlexCol>
        </FlexBox>
      )}

      {hasCoordinates && (
        <FlexCol gap={0}>
          <Text type="secondary">
            <small>Coordinates: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</small>
          </Text>
        </FlexCol>
      )}
    </FlexCol>
  );
};

export default DetailedAddressForm;

